import { Layout, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Footer } = Layout;
const { Text } = Typography;

const FOOTER_BG = '#0179C1';
/**
 * The WHITE KNOCKOUT wordmark, and it has to be.
 *
 * BMO's primary lockup (`bmo-gam-lockup-{en,fr}.svg`) is dark-on-transparent —
 * a blue wordmark and a red roundel — which is illegible on this blue. The
 * knockout is the only mark that works here, so a change of logo and a change
 * of background are the same decision. Per BMO's Figma the footer is ALWAYS
 * blue, so this stays.
 */
const LOGO_SRC = {
  en: '/bmo-global-asset-management.svg',
  fr: '/bmo-global-asset-management-fr.svg',
} as const;
// The French knockout is BMO's French lockup (supplied as `logo-fr.svg`, 2026-10-02) with its blue turned white — exactly how
// the English knockout relates to the English lockup — so the French page shows "Gestion mondiale d'actifs"
// (QA RIF TC-04-01: the English wordmark was shown in French).

/**
 * BMO-branded footer rendered at the bottom of every page. Blue band with the
 * regulatory disclaimer on the left and the BMO Global Asset Management
 * wordmark on the right. Same on every route and for every role — advisors and
 * public users included.
 *
 * ## The disclaimer is transcribed, not translated
 *
 * The French text is BMO's own copy and differs from the English in every
 * sentence — `une marque de commerce sous laquelle`, not a rendering of "a
 * brand name under which". An earlier version here read like a translation and
 * was wrong throughout. It also takes typographic apostrophes (U+2019). Treat
 * `footer.disclaimer` in both locale files as legal text supplied by BMO: it is
 * quoted, not authored, and `appFooter.test.tsx` pins it so a well-meaning
 * "improvement" cannot quietly reword a trademark attribution.
 *
 * Layout uses flex so on narrow viewports the wordmark wraps below the text
 * instead of overflowing — the disclaimer stays readable on every breakpoint.
 */
export function AppFooter() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr') ?? false;
  return (
    <Footer
      style={{
        background: FOOTER_BG,
        padding: '24px 32px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 32,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Text
          style={{
            flex: '1 1 600px',
            fontSize: 12,
            lineHeight: 1.55,
            // Fully opaque, NOT rgba(255,255,255,0.92). The 8% alpha composited
            // to #ebf4fa over FOOTER_BG for 4.18:1 — under the 4.5:1 that 12px
            // text needs (WCAG 1.4.3). Solid white on this blue is 4.66:1.
            color: '#ffffff',
            maxWidth: 920,
          }}
        >
          {t('footer.disclaimer')}
        </Text>
        <img
          src={isFr ? LOGO_SRC.fr : LOGO_SRC.en}
          alt={isFr ? 'BMO Gestion mondiale d’actifs' : 'BMO Global Asset Management'}
          style={{ height: 28, flexShrink: 0 }}
        />
      </div>
    </Footer>
  );
}
